import {
  CheckBoxOutlineBlank,
  CheckBoxRounded,
  Edit,
  ExpandMore,
  History,
  Save
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { Button, Table } from '../../../components';
import { formatDate } from '../../../helpers';
import { IDynamicFileMangementTemplate } from '../../../models';
import {
  getDynamicDocumentationFileTemplatesRecent,
  updateDynamicDocumentationFileTemplate,
  createPdfFromBuildDocTemplate
} from '../../../fetch';
import { DynamicFileManagementHistoryModal } from './dynamic-file-management-history-modal';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';

interface IDynamicFileManagementAccordionProps {
  file: string;
  isActive: boolean;
  refetchRecent: boolean;
}

export const DynamicFileManagementAccordion: FC<IDynamicFileManagementAccordionProps> = ({
  file,
  isActive,
  refetchRecent
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isContractHistoryModalOpen, setIsContractHistoryModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [recentTemplates, setRecentTemplates] = useState<IDynamicFileMangementTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchRecentTemplates = async () => {
    try {
      setIsLoading(true);
      const res = await getDynamicDocumentationFileTemplatesRecent(file);
      setRecentTemplates(res);
    } catch (error: any) {
      enqueueSnackbar(error?.Detail ?? `Error fetching recent templates.`, {
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded || refetchRecent) {
      fetchRecentTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, refetchRecent]);

  return (
    <>
      <Accordion key={file} expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            '&& .MuiAccordionSummary-content': {
              margin: 0
            }
          }}
        >
          <Box display="flex" alignItems="center" width="100%">
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {file}
            </Typography>
            {isActive && (
              <Typography variant="subtitle1" sx={{ mr: '1rem', color: 'primary.main' }}>
                Active
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: `0px 32px 8px`
          }}
        >
          <Divider sx={{ marginBottom: '0.5rem' }} />
          <Table
            columns={[
              {
                Header: 'Active',
                accessor: 'isActive',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: IDynamicFileMangementTemplate } };
                }) => {
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tooltip
                        title={
                          original.isDeletedInPandaDoc
                            ? 'Template is deleted in Pandadoc'
                            : original.isActive
                            ? 'Set as "Inactive"'
                            : 'Set as "Active"'
                        }
                      >
                        <span>
                          <IconButton
                            sx={{ padding: 0 }}
                            size="small"
                            disabled={original.isDeletedInPandaDoc}
                            onClick={async () => {
                              try {
                                setIsUpdating(true);
                                await updateDynamicDocumentationFileTemplate(
                                  original.pandaDocTemplateId,
                                  { isActive: !original.isActive }
                                );
                                // Refetchs this query for the active status on accordion
                                queryClient.invalidateQueries([
                                  'getDynamicDocumentationFileTemplates'
                                ]);
                                fetchRecentTemplates();
                                enqueueSnackbar(`File updated!`, {
                                  variant: 'success'
                                });
                              } catch (error: any) {
                                enqueueSnackbar(error?.Detail ?? `Error updating file.`, {
                                  variant: 'error'
                                });
                              } finally {
                                setIsUpdating(false);
                              }
                            }}
                            color="primary"
                          >
                            {original.isActive ? <CheckBoxRounded /> : <CheckBoxOutlineBlank />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                }
              },
              {
                Header: 'Created Date',
                accessor: 'createdDate',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: IDynamicFileMangementTemplate } };
                }) => {
                  const formattedDate = formatDate(original?.createdDate);
                  return <div>{formattedDate}</div>;
                }
              },
              {
                Header: 'File Name',
                accessor: 'documentName',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: IDynamicFileMangementTemplate } };
                }) => {
                  return <Typography>{original?.documentName}</Typography>;
                }
              },
              {
                Header: 'Created By',
                accessor: 'createdBy',
                sort: false
              },

              {
                Header: '',
                accessor: 'actions',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: IDynamicFileMangementTemplate } };
                }) => {
                  return (
                    <Box display="flex" gap={1}>
                      <Tooltip
                        title={
                          original.isDeletedInPandaDoc ? 'Template is deleted in Pandadoc' : ''
                        }
                      >
                        <span>
                          <Button
                            id="edit"
                            startIcon={<Edit />}
                            disabled={original.isDeletedInPandaDoc}
                            variant="text"
                            color="primary"
                            asLink
                            to={`/documentation/build-documentation/file-management/dynamic/${original.pandaDocTemplateId}`}
                          >
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                      {original.isActive && (
                        <Button
                          id="update"
                          startIcon={<Save />}
                          onClick={async () => {
                            try {
                              setIsUpdating(true);
                              await createPdfFromBuildDocTemplate(original.pandaDocTemplateId);
                              enqueueSnackbar('Template updated successfully', {
                                variant: 'success'
                              });
                            } catch (error: any) {
                              enqueueSnackbar(error?.Detail ?? 'Error updating template', {
                                variant: 'error'
                              });
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          variant="text"
                          color="primary"
                          disabled={isUpdating || original.isDeletedInPandaDoc}
                        >
                          Save Template
                        </Button>
                      )}
                    </Box>
                  );
                }
              }
            ]}
            data={recentTemplates ?? []}
            isLoading={isLoading || isUpdating}
            noResultsText="No results"
            hidePagination
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              id="contract-history"
              startIcon={<History />}
              onClick={() => setIsContractHistoryModalOpen(true)}
              variant="text"
              color="primary"
            >
              History
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
      {isContractHistoryModalOpen && (
        <DynamicFileManagementHistoryModal
          handleClose={() => setIsContractHistoryModalOpen(false)}
          documentName={file}
          fetchRecentTemplates={fetchRecentTemplates}
        />
      )}
    </>
  );
};
